import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import styles from "./WheelOfFortune.module.scss";
import { getSeededRandomNumber } from "../../utils/randomUtils"; // Funkce pro generování náhodného čísla s ohledem na seed

const WheelOfFortune = () => {
  const navigate = useNavigate(); // Navigace mezi stránkami
  const [newOption, setNewOption] = useState(""); // Nová možnost, která se přidává do kola štěstí
  const [options, setOptions] = useState([]); // Seznam možností na kole štěstí
  const [selected, setSelected] = useState(null); // Vybraný výherce
  const [spinning, setSpinning] = useState(false); // Stav otáčení kola
  const [rotation, setRotation] = useState(0); // Aktuální rotace kola
  const [autoDelete, setAutoDelete] = useState(false); // Jestli se má možnost automaticky odstranit po výběru
  const [showModal, setShowModal] = useState(false); // Stav pro zobrazení modálního okna s výsledkem

  // Předdefinované barvy pro segmenty kola
  const defaultColors = [
    "#e74c3c",
    "#3498db",
    "#2ecc71",
    "#f1c40f",
    "#9b59b6",
    "#e67e22",
    "#1abc9c",
    "#e84393",
  ];

  // Funkce pro přidání nové možnosti do seznamu
  const addOption = () => {
    if (newOption.trim() !== "") {
      // Pokud je nová možnost platná (ne prázdná), přidá ji do seznamu
      const newOptions = newOption.includes(",")
        ? newOption
            .split(",")
            .map((opt) => opt.trim())
            .filter(Boolean)
        : [newOption.trim()];
      setOptions([...options, ...newOptions]);
      setNewOption(""); // Vyprázdní vstupní pole
    }
  };

  // Funkce pro odstranění možnosti ze seznamu
  const removeOption = (index) => {
    setOptions(options.filter((_, i) => i !== index)); // Odstraní možnost podle indexu
  };

  // Funkce pro přidání možnosti stisknutím klávesy Enter
  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      addOption(); // Přidá novou možnost při stisku Enter
    }
  };

  // Funkce pro nahrání souboru s možnostmi (txt)
  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file && file.type === "text/plain") {
      const reader = new FileReader();
      reader.onload = (event) => {
        const text = event.target.result;
        const fileOptions = text
          .split(/,|\r?\n/)
          .map((item) => item.trim())
          .filter(Boolean); // Rozdělí text na možnosti a přidá je do seznamu
        setOptions([...options, ...fileOptions]);
      };
      reader.readAsText(file); // Čte soubor jako text
    }
  };

  // Funkce pro roztočení kola
  const handleSpin = async () => {
    if (options.length < 2 || spinning) return; // Pokud není dost možností nebo je kolo již roztočeno

    setSpinning(true); // Aktivuje stav otáčení
    const numOptions = options.length;
    const segmentAngle = 360 / numOptions; // Určuje úhel pro každý segment kola
    const randomIndex = await getSeededRandomNumber(numOptions); // Získá náhodný index pro výběr
    const randomOffset = Math.random() * segmentAngle; // Přidá náhodný offset pro plynulost otáčení
    const winningAngle = randomIndex * segmentAngle + randomOffset; // Výherní úhel
    const currentRotationMod = rotation % 360;
    const targetRotationMod = (360 - winningAngle) % 360;
    let delta = (targetRotationMod - currentRotationMod + 360) % 360; // Vypočítá potřebný úhel pro dokončení otáčení
    if (delta < 1) {
      delta += 360; // Pokud je delta menší než 1, přidá 360, aby kolo dokončilo otáčení
    }
    const extraSpins = (Math.floor(Math.random() * 5) + 8) * 360; // Přidá náhodný počet extra otáček
    const finalRotation = rotation + extraSpins + delta; // Celková rotace
    setRotation(finalRotation); // Nastaví rotaci kola

    // Po 8 sekundách (doba otáčení) zobrazení výherce
    setTimeout(() => {
      setSelected(options[randomIndex]);
      setShowModal(true); // Zobrazí modální okno s výhercem
      if (autoDelete) {
        setOptions((prev) => prev.filter((_, i) => i !== randomIndex)); // Pokud je zapnuto autoDelete, odstraní vybranou možnost
      }
      setSpinning(false); // Kolo je hotovo
    }, 8000);
  };

  // Funkce pro zavření modálního okna
  const closeModal = () => {
    setShowModal(false);
    setSelected(null); // Vymaže vybranou možnost
  };

  // Nastavení stylu pro kolo štěstí (dynamicky podle možností)
  const wheelStyle = {};
  if (options.length > 0) {
    const numOptions = options.length;
    const segmentAngle = 360 / numOptions;
    const gradientParts = options.map((option, index) => {
      const color = defaultColors[index % defaultColors.length]; // Barvy segmentů kola
      const startAngle = index * segmentAngle;
      const endAngle = (index + 1) * segmentAngle;
      return `${color} ${startAngle}deg ${endAngle}deg`; // Vytvoří barevný přechod pro každý segment
    });
    wheelStyle.background = `conic-gradient(${gradientParts.join(", ")})`; // Nastaví pozadí jako konický gradient
  } else {
    wheelStyle.background = "#ccc"; // Pokud nejsou možnosti, nastaví šedé pozadí
  }

  return (
    <div className={styles.container}>
      <button className={styles.backButton} onClick={() => navigate("/")}>
        Zpět
      </button>
      <h2 className={styles.title}>Kolo štěstí</h2>
      <div className={styles.inputContainer}>
        {/* Vstup pro přidání nové možnosti */}
        <input
          type="text"
          placeholder="Přidejte možnost (nebo více oddělených čárkou)"
          value={newOption}
          onChange={(e) => setNewOption(e.target.value)}
          onKeyPress={handleKeyPress}
          className={styles.input}
        />
        <button onClick={addOption} className={styles.addButton}>
          Přidat
        </button>
      </div>
      <input
        type="file"
        accept=".txt"
        onChange={handleFileUpload}
        className={styles.fileInput}
      />
      {/* Přepínač pro automatické mazání po vylosování */}
      <div className={styles.autoDelete}>
        <label htmlFor="autoDeleteSwitch">
          Automatické vymazání položky po vylosování:
        </label>
        <input
          type="checkbox"
          id="autoDeleteSwitch"
          checked={autoDelete}
          onChange={() => setAutoDelete(!autoDelete)}
        />
      </div>
      {/* Zobrazení seznamu možností */}
      {options.length > 0 && (
        <div className={styles.options}>
          {options.map((option, index) => (
            <div key={index} className={styles.option}>
              <span>{option}</span>
              <button
                className={styles.deleteButton}
                onClick={() => removeOption(index)}
              >
                ×
              </button>
            </div>
          ))}
        </div>
      )}
      {/* Zobrazení kola štěstí */}
      <div className={styles.wheelWrapper}>
        <div className={styles.arrow}></div>
        <div
          className={styles.wheel}
          style={{ ...wheelStyle, transform: `rotate(${rotation}deg)` }}
        >
          {options.map((option, index) => {
            const numOptions = options.length;
            const segmentAngle = 360 / numOptions;
            const midAngle = index * segmentAngle + segmentAngle / 2;
            const dynamicFontSize = `${Math.max(
              0.9 - numOptions * 0.05,
              0.6
            )}rem`;
            const labelStyle = {
              transform: `translate(-50%, -50%) rotate(${midAngle}deg) translateY(-90px)`,
              fontSize: dynamicFontSize,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            };
            return (
              <div
                key={index}
                className={styles.segmentLabel}
                style={labelStyle}
              >
                {option}
              </div>
            );
          })}
        </div>
      </div>
      {/* Tlačítko pro roztočení kola */}
      <button
        onClick={handleSpin}
        className={styles.spinButton}
        disabled={spinning || options.length < 2}
      >
        {spinning ? "Roztočeno..." : "Roztočit kolo"}
      </button>

      {/* Modální okno pro zobrazení výherce */}
      {showModal && selected && (
        <div className={styles.modalOverlay}>
          <div className={styles.modal}>
            <button className={styles.modalClose} onClick={closeModal}>
              &times;
            </button>
            <h2 className={styles.modalTitle}>🎉 Výherce!</h2>
            <p className={styles.modalText}>{selected}</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default WheelOfFortune;
